import { PropertiesPanel } from '../../../../../../widgets/propertiesPanel';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LIGHT_KIND, LightConfigHelpers, LightNode, useLightPropertiesCreator } from '../lightsEditorConfig';
import InspectorShell from '../inspectorShell';

// L'involucro sta in `InspectorShell`, uno per tutti i generi: qui resta ciò che è solo di
// una luce.

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Interfaccia semplificata - riceve solo l'oggetto light completo
export interface LightOverlayProps {
    light: {
        id: string;
        name: string;
        lightType: string;
        active?: boolean;
        intensity: number;
        diffuseColor: string;

        groundColor?: string;
        position?: [number, number, number];
        direction?: [number, number, number];
        angle?: number;
        exponent?: number;
    };
    onUpdate: (updates: Partial<LightOverlayProps['light']>) => void;
    /** Cambia la specie della luce: dietro, il motore la rifà. */
    onChangeType: (lightType: string) => void;
}

// Due terne uguali sono la stessa terna: `[0,1,0]` letto due volte sono due array diversi, e un
// confronto per identità direbbe «cambiato» a ogni respiro.
const same = (a: any, b: any) => (Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((value, index) => value === b[index]) : a === b);

const LightsOverlayComponent: FC<LightOverlayProps> = ({ light, onUpdate, onChangeType }) => {
    // Hook per creare le proprietà delle luci
    const createLightProperties = useLightPropertiesCreator();

    // Stato interno per le proprietà della luce - pattern MaterialOverlayComponent/ObjectOverlayComponent
    const [localLight, setLocalLight] = useState<LightNode>(light as LightNode);

    // LO STATO INTERNO SERVE A NON PERDERE QUELLO CHE SI STA SCRIVENDO: un campo riscritto da
    // fuori mentre ci si digita dentro è inservibile. Ma si risincronizzava SOLO cambiando luce, e
    // così tutto ciò che cambiava da fuori restava fuori — spegnere una luce dall'occhio della sua
    // riga lasciava «Active» acceso qui: due segni per lo stesso stato, che raccontavano due storie.
    //
    // Il confronto giusto è con l'ULTIMA LUCE RICEVUTA, non con quella che si vede a schermo: si
    // adotta ciò che è cambiato davvero là fuori, e un campo che da fuori non ha toccato nessuno
    // resta com'è, con dentro quello che si sta scrivendo.
    const lastLightRef = useRef(light);

    useEffect(() => {
        const previous = lastLightRef.current;
        lastLightRef.current = light;

        if (previous.id !== light.id) {
            setLocalLight(light as LightNode);
            return;
        }

        const changed = (Object.keys(light) as (keyof LightNode)[]).filter((key) => !same(light[key], (previous as any)[key]));
        if (!changed.length) return;

        setLocalLight((prev) => {
            const next: any = { ...prev };
            changed.forEach((key) => (next[key] = (light as any)[key]));
            return next;
        });
    }, [light]);

    const currentValues = useMemo(
        () => ({
            ...localLight,
            name: localLight.name || 'Unnamed Light',
            intensity: typeof localLight.intensity === 'number' ? localLight.intensity : 1,
            diffuseColor: normalizeHexColor(localLight.diffuseColor),

            groundColor: normalizeHexColor(localLight.groundColor || '#000000'),
            position: localLight.position || ([0, 0, 0] as [number, number, number]),
            direction: localLight.direction || ([0, -1, 0] as [number, number, number]),
            angle: typeof localLight.angle === 'number' ? localLight.angle : 30,
            exponent: typeof localLight.exponent === 'number' ? localLight.exponent : 1
        }),
        [localLight]
    );

    // Helper per aggiornare sia lo stato locale che quello esterno - pattern ObjectOverlayComponent
    const updateProperty = useCallback(
        <K extends keyof LightNode>(key: K, value: LightNode[K]) => {
            // Aggiorna lo stato locale immediatamente per UI responsiva
            setLocalLight((prev) => ({
                ...prev,
                [key]: value
            }));

            // Valida i colori prima di inviarli
            if (key === 'diffuseColor' || key === 'groundColor') {
                const normalizedColor = normalizeHexColor(value as string);
                onUpdate({ [key]: normalizedColor } as any);
            } else {
                onUpdate({ [key]: value } as any);
            }
        },
        [onUpdate]
    );

    // Funzione per aggiornare vettori (posizione/direzione) - pattern ObjectOverlayComponent
    const updateVector = useCallback(
        (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => {
            const currentVector = currentValues[vectorType];
            const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
            const newVector = [...currentVector] as [number, number, number];
            newVector[axisIndex] = value;

            updateProperty(vectorType, newVector);
        },
        [currentValues, updateProperty]
    );

    // Handler per il cambio di colore - gestisce l'interfaccia ColorSelection
    const handleColorChange = useCallback(
        (key: 'diffuseColor' | 'groundColor') => {
            return (_: any, color: string) => {
                updateProperty(key, color);
            };
        },
        [updateProperty]
    );

    // AZZERARE È UNA COSA SOLA su due terne: al centro della scena, e rivolta dove il suo verso
    // «normale» è. Per quasi tutte è **in giù** — una luce che illumina dal cielo — ma per una
    // emisferica la direzione è dove sta l'ALTO, e rivolgerla in giù la ribalterebbe: il colore del
    // cielo finirebbe sotto i piedi. Quindi in su.
    //
    // Passa da qui e non da due `updateProperty` in fila perché deve arrivare come UN aggiornamento:
    // due di seguito sono due ridisegni e due passaggi nel motore per un gesto solo.
    const resetTransform = useCallback(() => {
        const up = localLight.lightType === 'hemispheric_light';
        const reset = {
            position: [0, 0, 0] as [number, number, number],
            direction: (up ? [0, 1, 0] : [0, -1, 0]) as [number, number, number]
        };

        setLocalLight((prev) => ({ ...prev, ...reset }));
        onUpdate(reset);
    }, [localLight.lightType, onUpdate]);

    // Crea gli helpers per la configurazione delle proprietà
    const helpers: LightConfigHelpers = useMemo(
        () => ({
            updateProperty,
            updateVector,
            handleColorChange,
            resetTransform,
            changeType: onChangeType
        }),
        [updateProperty, updateVector, handleColorChange, resetTransform, onChangeType]
    );

    // Genera le proprietà usando il factory pattern
    const properties = useMemo(() => {
        return createLightProperties(currentValues, helpers);
    }, [createLightProperties, currentValues, helpers]);

    return (
        <InspectorShell
            kind='light'
            name={currentValues.name}
            specs={LIGHT_KIND[currentValues.lightType] ?? currentValues.lightType}
            onRename={(name) => updateProperty('name', name)}
        >
            <PropertiesPanel properties={properties} />
        </InspectorShell>
    );
};

export default LightsOverlayComponent;
